/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

if (!customElements.get('color-catalog')) {
  class ColorCatalog extends HTMLElement {
    constructor() {
      super();
      this.data = null;
      this.selectedColor = 'purple';
      this.promotionIndex = 1;
      this.promotionTimer = null;
    }

    async connectedCallback() {
      this.range = this.querySelector('[data-color-range]');
      this.label = this.querySelector('[data-color-label]');
      this.products = Array.from(this.querySelectorAll('[data-color-product]'));
      this.empty = this.querySelector('[data-color-empty]');
      this.promotionSlides = Array.from(this.querySelectorAll('[data-promotion-slide]'));

      try {
        const response = await fetch(this.dataset.dataUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Color data request failed with ${response.status}`);
        this.data = await response.json();
      } catch (error) {
        console.error(error);
        return;
      }

      this.selectedColor = this.getColorFromLocation();
      this.setRangeFromColor(this.selectedColor);
      this.applyFilter(false);
      this.updateBrandLinks();

      this.range?.addEventListener('input', this.handleRangeInput);
      this.range?.addEventListener('change', this.handleRangeChange);
      this.startPromotions();
    }

    disconnectedCallback() {
      this.range?.removeEventListener('input', this.handleRangeInput);
      this.range?.removeEventListener('change', this.handleRangeChange);
      window.clearInterval(this.promotionTimer);
    }

    handleRangeInput = () => {
      const configuration = this.getConfiguration(Number(this.range.value));
      if (!configuration) return;
      this.selectedColor = configuration.color;
      this.applyFilter(false);
    };

    handleRangeChange = () => {
      this.applyFilter(true);
    };

    getColorFromLocation() {
      const configuredGroups = this.data?.brands?.[this.dataset.brand]?.groups || {};
      const queryColor = new URL(window.location.href).searchParams.get('color');
      const pathColor = window.location.pathname.split('/').filter(Boolean)[2];
      const requested = queryColor || pathColor || 'purple';
      return Object.hasOwn(configuredGroups, requested) ? requested : 'purple';
    }

    getConfiguration(value) {
      return this.data?.filterMap?.find((configuration) => value <= configuration.max) || null;
    }

    getConfigurationForColor(color) {
      return [...(this.data?.filterMap || [])]
        .reverse()
        .find((configuration) => configuration.color === color) || null;
    }

    setRangeFromColor(color) {
      const configuration = this.getConfigurationForColor(color);
      if (configuration && this.range) this.range.value = String(configuration.max);
    }

    applyFilter(updateLocation) {
      const brand = this.data?.brands?.[this.dataset.brand];
      if (!brand) return;

      const configuration = this.getConfigurationForColor(this.selectedColor);
      const allowedSkus = new Set(brand.groups?.[this.selectedColor] || []);
      let visibleCount = 0;

      this.products.forEach((product) => {
        const visible = allowedSkus.has(product.dataset.sku || '');
        product.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.label) this.label.textContent = this.selectedColor.replaceAll('-', ' ');
      if (configuration) this.style.setProperty('--color-filter-thumb', configuration.rgba);
      if (this.empty) this.empty.hidden = visibleCount !== 0;

      if (updateLocation) {
        const url = new URL(window.location.href);
        const klaviyo = url.searchParams.get('_kx') || window.sessionStorage.getItem('_kx');
        const publicPath = `/pages/colors-${this.dataset.brand}`;
        const params = new URLSearchParams({ color: this.selectedColor });
        if (klaviyo) params.set('_kx', klaviyo);
        window.history.replaceState({}, '', `${publicPath}?${params}`);
        this.updateBrandLinks();
      }
    }

    updateBrandLinks() {
      const url = new URL(window.location.href);
      const klaviyo = url.searchParams.get('_kx') || window.sessionStorage.getItem('_kx');
      this.querySelectorAll('[data-brand-link]').forEach((link) => {
        const brand = link.dataset.brandLink;
        const params = new URLSearchParams({ color: this.selectedColor });
        if (klaviyo) params.set('_kx', klaviyo);
        link.href = `/pages/colors-${brand}?${params}`;
      });
    }

    startPromotions() {
      if (this.promotionSlides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.showPromotion(this.promotionIndex);
      this.promotionTimer = window.setInterval(() => {
        this.promotionIndex = (this.promotionIndex + 1) % this.promotionSlides.length;
        this.showPromotion(this.promotionIndex);
      }, 2500);
    }

    showPromotion(index) {
      this.promotionSlides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
        slide.classList.toggle('is-previous', slideIndex < index);
      });
    }
  }

  customElements.define('color-catalog', ColorCatalog);
}
